import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, FileText } from 'lucide-react';

import { SourceIdentity } from '@knitknot/shared/components/report';
import { Button } from '@knitknot/shared/components/ui/button';
import { DeltaIndicator } from '@knitknot/shared/components/ui/delta-indicator';
import { EmptyState } from '@knitknot/shared/components/ui/empty-state';
import { SECTION_ACTION_CLASS, SectionHeader } from '@knitknot/shared/components/ui/section-header';
import { SimpleTooltip } from '@knitknot/shared/components/ui/simple-tooltip';
import { Skeleton } from '@knitknot/shared/components/ui/skeleton';
import { DataTable, type DataTableColumn } from '@knitknot/shared/components/ui/data-table';
import { formatDelta } from '@knitknot/shared/components/report/metrics';
import { normalizeUrl, pagePath, soleHost } from '@knitknot/shared/utils';
import { useOwnedMovers, type OwnedMoverRow } from '@/hooks/api/useSources';
import { formatWeekOf, usePeriods } from '@/hooks/api/usePeriods';
import { useWorkspaceStore } from '@/hooks/state';
import { consoleRoutes } from '@/lib/routes';
import { PlaybookPageBadge } from '@/components/playbook/PlaybookPageBadge';
import { PromptLensToggle } from '@/components/sources/PromptLensToggle';
import { lensCategory, type PromptLens } from '@/components/sources/promptLens';
import type { PlaybookListItem } from '@/types/playbooks';

/** Matches the sibling rails on /home (rule 5 — paired lists cap at the same count). */
const MAX_ROWS = 5;

/**
 * Which plays shipped each URL — keyed on the SAME canonical identity the
 * `url_hash` is built from (`normalizeUrl`, the client mirror of the backend's
 * `core/sources/url_identity.py`), so this agrees with the `playbooks` field
 * /sources gets from the server rather than guessing at a second match rule.
 *
 * It is derived client-side because the `owned-movers` payload carries no
 * playbook link and /home already holds the workspace's plays — one fetch, four
 * consumers. A play with no `shipped_url` has no page to match and is skipped;
 * "exact shipped-page match" is the whole claim the badge makes.
 */
function playbooksByUrl(playbooks: PlaybookListItem[]): Map<string, PlaybookListItem[]> {
  const byUrl = new Map<string, PlaybookListItem[]>();
  for (const play of playbooks) {
    const key = normalizeUrl(play.shipped_url ?? '');
    if (!key) continue;
    byUrl.set(key, [...(byUrl.get(key) ?? []), play]);
  }
  return byUrl;
}

/**
 * The owned pages AI cited most in the selected benchmark (latest by default).
 *
 * This replaced "Needs attention" — a top-5 of open ISSUES — on 2026-09-04 (Max:
 * *"instead of 'needs attention' we should show the top cited pages that week, like
 * the 'Your pages' in the period page pretty much"*). Two reasons it is the better
 * rail: /home already carries the problem side of the story twice (the Latest
 * benchmark band's regressions, and Playbooks below), and an issue list ranked by
 * reach is a queue the /issues page owns in full. What /home lacked was the
 * receipt — which of the customer's own pages AI is actually reading right now.
 *
 * It is the period page's "Your pages" table at /home scale: the same two facts
 * (citing answers, and the change since last week) from the same canonical
 * window-scoped `COUNT(DISTINCT response_id)`, so a page reads the same number on
 * both surfaces.
 */
export function TopCitedPagesCard({ playbooks = [] }: { playbooks?: PlaybookListItem[] }) {
  const navigate = useNavigate();
  const { workspaceId } = useWorkspaceStore();
  const periodsQ = usePeriods();
  const [selection, setSelection] = useState<{ workspaceId: string | null; periodId: string } | null>(null);
  const periodId = selection?.workspaceId === workspaceId ? selection?.periodId : undefined;
  // The same All | Visibility | Head-to-head lens the period page's "Your
  // pages" ledger carries, so a page reads the same count on both surfaces
  // under the same cut. Server-side: it is a different row SET per lens.
  //
  // VISIBILITY is the resting lens (Max, 2026-09-18). `all` mixed two
  // populations in one count: head-to-head prompts name the company by
  // construction, so a page's `all` number is part organic discovery and part
  // an artefact of the question. Visibility is the cut that answers "which of
  // our pages is AI reading when nobody prompted it with our name"; the other
  // two stay one click away.
  const [lens, setLens] = useState<PromptLens>('visibility');
  const pagesQ = useOwnedMovers('cited', lensCategory(lens), periodId);
  const periods = useMemo(
    () => (periodsQ.data ?? []).filter((period) => period.eval_count > 0)
      .sort((a, b) => b.period_start.localeCompare(a.period_start)),
    [periodsQ.data],
  );
  const currentId = periodId ?? pagesQ.data?.current_period?.period_id;
  const currentIndex = periods.findIndex((period) => period.id === currentId);
  const previous = currentIndex >= 0 ? periods[currentIndex + 1] : undefined;
  const next = currentIndex > 0 ? periods[currentIndex - 1] : undefined;
  const currentDate = periods[currentIndex]?.period_start ?? pagesQ.data?.current_period?.date;
  const selectPeriod = (id: string) => setSelection({ workspaceId, periodId: id });
  const pageHref = (urlHash: string) => periodId
    ? consoleRoutes.benchmarks.periodOwnedPage(periodId, urlHash)
    : consoleRoutes.publish.page(urlHash);
  const rows = (pagesQ.data?.movers ?? []).slice(0, MAX_ROWS);
  // Owned pages are one domain by construction, so the repeated host would eat the
  // path — the only part that distinguishes the rows (rule 69's sole-host gate).
  // Gated on the rows actually rendered, which IS the full set here.
  const host = soleHost(rows.map((row) => row.url));
  const shippedBy = useMemo(() => playbooksByUrl(playbooks), [playbooks]);

  const columns: DataTableColumn<OwnedMoverRow>[] = [
    {
      key: 'page',
      header: 'Page',
      width: 'minmax(200px,1fr)',
      // Identity ONLY — the ROW is the anchor (`rowHref` below). Wrapping this
      // in its own `<Link>` put `SourceIdentity external`'s trailing
      // open-in-a-new-tab anchor inside another anchor: invalid HTML, and it
      // kills the row's own ⌘-click. `external` is documented for exactly this
      // shape — "use THAT when the surrounding row/link already navigates to the
      // cited-source detail, so the identity needs no navigation of its own and
      // only the escape hatch is missing".
      render: (row) => {
        // The SAME marker /sources puts on a shipped page — `PlaybookPageBadge`,
        // reused rather than re-invented (rules 4 + 51), so "this page exists
        // because of a play" reads identically on both surfaces, carries the
        // same no-causation tooltip, and opens the same play.
        const plays = shippedBy.get(normalizeUrl(row.url)) ?? [];
        return (
          <div className="flex min-w-0 items-center gap-2">
            <SourceIdentity url={row.url} label={host ? pagePath(row.url, host) : undefined} external />
            {plays.length > 0 && (
              <PlaybookPageBadge
                titles={plays.map((play) => play.title)}
                onClick={() => navigate(consoleRoutes.publish.playbook(plays[0].id))}
              />
            )}
          </div>
        );
      },
    },
    {
      key: 'cited',
      header: 'Citing answers',
      headerLabel: 'Answers citing the page in this benchmark',
      width: '112px',
      align: 'right',
      cellClassName: 'tabular-nums text-foreground',
      render: (row) => row.current_cited.toLocaleString(),
    },
    {
      key: 'change',
      header: 'Change',
      headerLabel: 'Change in citing answers',
      width: '84px',
      align: 'right',
      render: (row) => pagesQ.data?.previous_period ? (
        <SimpleTooltip content={`${row.previous_cited.toLocaleString()} in the previous benchmark`}>
          <span className="inline-flex items-center justify-end">
            <DeltaIndicator delta={formatDelta(row.delta, '')} />
          </span>
        </SimpleTooltip>
      ) : null,
    },
  ];

  return (
    <section className="flex min-h-0 flex-col" aria-label="Top cited pages">
      <SectionHeader
        title="Top cited pages"
        className="flex-wrap [&>div:first-child]:flex-wrap"
        hint={
          <span className="inline-flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Previous benchmark"
              title="Previous benchmark"
              disabled={!previous}
              onClick={() => previous && selectPeriod(previous.id)}
            >
              <ChevronLeft aria-hidden />
            </Button>
            <span className="whitespace-nowrap" aria-live="polite">
              {currentDate ? formatWeekOf(currentDate) : 'Latest measured benchmark'}
            </span>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Next benchmark"
              title="Next benchmark"
              disabled={!next}
              onClick={() => next && selectPeriod(next.id)}
            >
              <ChevronRight aria-hidden />
            </Button>
          </span>
        }
        action={
          <span className="flex items-center gap-3">
            <PromptLensToggle value={lens} onChange={setLens} />
            <Link to={periodId ? consoleRoutes.benchmarks.period(periodId) : consoleRoutes.publish.pages} className={SECTION_ACTION_CLASS} aria-label="View all your pages">
              View all
              <ArrowRight className="h-3 w-3" aria-hidden />
            </Link>
          </span>
        }
      />
      <div className="mt-2">
        {pagesQ.isError ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">Cited pages are temporarily unavailable.</p>
        ) : pagesQ.isLoading ? (
          <Skeleton className="h-40" aria-label="Loading cited pages" />
        ) : rows.length > 0 ? (
          // `flush` + `sticky={false}` — the same props the sibling
          // Head-to-head rail carries. Without `flush` the table paints its own
          // `rounded-lg border bg-card` INSIDE a band that is already `bg-card`,
          // which the surface system bans outright ("a raised card never nests
          // another raised card") and which read as an extra box around this one
          // rail while its siblings had none.
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(row) => row.url_hash}
            // `url_hash` is on the payload, so the row's address is synchronous —
            // the cell was resolving the same hash again with `useUrlHash`.
            rowHref={(row) => pageHref(row.url_hash)}
            // `rowContainsLinks` keeps the row a <div> (the identity's external
            // icon is its own anchor), so `rowHref` covers only ⌘/middle-click —
            // a PLAIN click still needs `onRowClick`, exactly as the sibling
            // Head-to-head rail wires it.
            onRowClick={(row) => navigate(pageHref(row.url_hash))}
            rowContainsLinks
            sort={null}
            sticky={false}
            flush
          />
        ) : (
          <EmptyState
            icon={FileText}
            title={lens === 'all' ? 'No owned page was cited in this benchmark' : 'No owned page was cited in this benchmark and lens'}
            compact
          />
        )}
      </div>
    </section>
  );
}
